import { Grade } from '../components/_molecules/SettingsModalDesigner/SettingsModalDesigner';
import {
  ALLOWED_GRADES_BY_SCOPE,
  PHONICS_OBJECTIVES_BY_SCOPE,
  PhonicsObjective as PhonicsObjectiveSource,
  SCOPE_AND_SEQUENCE_OPTIONS,
} from '../components/dynamicPages/CreatePage/CreateFlow/Levels/phonicsWizardObjectives';

/** Re-exported for convenience in pages. */
export type PhonicsObjective = PhonicsObjectiveSource;

export type CurriculumEntry = {
  /** URL slug used in [curriculum] route. */
  slug: string;
  /** Canonical curriculum value matching keys in PHONICS_OBJECTIVES_BY_SCOPE. */
  value: string;
  /** Short label as shown in the wizard dropdown. */
  label: string;
  /** Full display name for marketing copy. */
  displayName: string;
};

const CURRICULUM_SLUG_BY_VALUE: Record<string, string> = {
  'Bluebonnet Learning': 'bluebonnet-learning',
  'UFLI Foundations': 'ufli-foundations',
  'Amplify CKLA': 'amplify-ckla',
  'Heggerty Phonemic Awareness': 'heggerty-phonemic-awareness',
  'Wilson Fundations': 'wilson-fundations',
  Wonders: 'wonders',
  'HMH Into Reading': 'hmh-into-reading',
  'Really Great Reading Phonics Boost': 'really-great-reading',
  'Savvas myView': 'savvas-myview',
};

export const CURRICULA: CurriculumEntry[] = SCOPE_AND_SEQUENCE_OPTIONS.map(
  ({ value, label }) => ({
    slug: CURRICULUM_SLUG_BY_VALUE[value] ?? slugifyValue(value),
    value,
    label,
    displayName: value,
  }),
);

export function getCurriculumBySlug(slug: string): CurriculumEntry | undefined {
  return CURRICULA.find(c => c.slug === slug);
}

export type LandingObjective = {
  /** Stable numeric ID — position in the flat per-curriculum list of non-disabled objectives. */
  id: number;
  curriculumValue: string;
  /**
   * Either a specific Grade (`'K'`, `'1'`, …) for grade-tagged lessons, or a
   * curriculum-range marker (`'K-5'`, `'Pre-K – 2'`) for curricula whose
   * lessons span the full curriculum. HMH Into Reading reuses one shared
   * lesson array across every grade key, so its lessons get the range
   * marker rather than a misleading "Kindergarten" tag.
   */
  grade: Grade | string;
  unitNumber?: number;
  lessonNumber: number | string;
  value: string;
  label: string;
  /** Slug for [targetSound] route: `${encodeURIComponent(value)}-${id}`. */
  slug: string;
  /** Five example words that obey the target rule; undefined for objectives
   *  with non-decodable / descriptive rules. */
  exampleWords?: string[];
};

/**
 * Walk a curriculum's grade map in the same order as ALLOWED_GRADES_BY_SCOPE
 * to keep IDs deterministic across builds. Skip disabled rows and rows
 * without an extractable rule.
 */
export function getObjectivesForCurriculum(curriculumValue: string): LandingObjective[] {
  const byGrade = PHONICS_OBJECTIVES_BY_SCOPE[curriculumValue];
  const grades = ALLOWED_GRADES_BY_SCOPE[curriculumValue];
  if (!byGrade || !grades) return [];

  // Detect curricula where every grade key points at the same lesson array.
  // HMH Into Reading does this — its phonics sequence isn't grade-specific,
  // it spans the curriculum's full range. For these we use the curriculum
  // range as the grade marker so cards / chips read e.g. "K-5" instead of
  // a misleading "Kindergarten".
  const sharedRangeLabel = detectSharedAcrossGrades(byGrade, grades)
    ? formatCurriculumRange(grades)
    : null;

  const out: LandingObjective[] = [];
  // Dedupe by object identity. Shared-array curricula would otherwise emit
  // each lesson once per grade — multiplying ~63 HMH lessons into ~378
  // entries with bogus grade tags.
  const seen = new Set<PhonicsObjective>();
  let nextId = 1;
  for (const grade of grades) {
    const list = byGrade[grade];
    if (!list) continue;
    for (const obj of list) {
      if (seen.has(obj)) continue;
      seen.add(obj);
      if (obj.disabled) continue;
      if (!hasExtractableRule(obj.value)) continue;
      const id = nextId++;
      out.push({
        id,
        curriculumValue,
        grade: sharedRangeLabel ?? grade,
        unitNumber: obj.unitNumber,
        lessonNumber: obj.lessonNumber,
        value: obj.value,
        label: obj.label,
        slug: slugifyObjective(obj.value, id),
        // Omit when undefined so the result is JSON-serialisable through
        // getStaticProps (Next.js rejects undefined values).
        ...(obj.exampleWords ? { exampleWords: obj.exampleWords } : {}),
      });
    }
  }
  return out;
}

function detectSharedAcrossGrades(
  byGrade: Partial<Record<Grade, PhonicsObjective[]>>,
  grades: Grade[],
): boolean {
  const arrays = grades.map(g => byGrade[g]).filter(Boolean) as PhonicsObjective[][];
  if (arrays.length <= 1) return false;
  return arrays.every(arr => arr === arrays[0]);
}

function formatCurriculumRange(grades: Grade[]): string {
  if (grades.length === 0) return '';
  if (grades.length === 1) return grades[0];
  return `${grades[0]}–${grades[grades.length - 1]}`;
}

export function getObjectiveById(
  curriculumValue: string,
  id: number,
): LandingObjective | undefined {
  return getObjectivesForCurriculum(curriculumValue).find(o => o.id === id);
}

/**
 * Look up the previous and next objectives in the curriculum's
 * scope-and-sequence relative to `id`. Returns `null` for either when
 * `id` is at the start or end of the sequence — the per-objective page
 * uses that signal to link those cards back to the curriculum root
 * instead.
 */
export function getAdjacentObjectives(
  curriculumValue: string,
  id: number,
): { prev: LandingObjective | null; next: LandingObjective | null } {
  const list = getObjectivesForCurriculum(curriculumValue);
  const index = list.findIndex(o => o.id === id);
  if (index === -1) return { prev: null, next: null };
  return {
    prev: index > 0 ? list[index - 1] : null,
    next: index < list.length - 1 ? list[index + 1] : null,
  };
}

export function slugifyObjective(value: string, id: number): string {
  // Build a URL-safe, human-readable slug. Next.js dynamic routes can't
  // handle encoded slashes (%2F), so we lowercase + replace any non-
  // alphanumeric run with `-`. The trailing `-<id>` keeps lookup
  // unambiguous when the same value appears in multiple lessons.
  const readable = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${readable || 'objective'}-${id}`;
}

/**
 * Pull the trailing numeric `-<id>` off the slug. The leading portion is the
 * readable value; we only need the ID for lookup.
 */
export function parseObjectiveSlug(
  slug: string,
): { id: number; readable: string } | undefined {
  const m = slug.match(/^(.+)-(\d+)$/);
  if (!m) return undefined;
  const id = Number(m[2]);
  if (!Number.isFinite(id)) return undefined;
  return { id, readable: m[1] };
}

/**
 * Build the human-readable rule phrase that goes into the slide and the H1.
 * Examples:
 *   "/m/ > 'm'"                          -> "/m/ ('m')"
 *   "/er/ > 'er' and /est/ > 'est'"      -> "/er/ ('er') and /est/ ('est')"
 *   "/y/ > 'y' + Tricky Word: Yellow"    -> "/y/ ('y')"
 *   "Spell one-syllable, short-vowel words" -> "Spell one-syllable, short-vowel words"
 *   "ar"                                 -> "ar"
 */
export function extractRuleFromValue(value: string): string {
  const cleaned = value.split(/\s+\+\s+/)[0].trim();
  if (/^\/[^/]+\/\s*>\s*'[^']+'/.test(cleaned)) {
    return cleaned.replace(/\/([^/]+)\/\s*>\s*'([^']+)'/g, "/$1/ ('$2')");
  }
  return cleaned;
}

/**
 * Filter out rows that are too thin to merit their own SEO page — these are
 * usually review or non-phonemic objectives where extracting a clean rule
 * isn't possible.
 */
function hasExtractableRule(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  // Reject pure-review entries (no concrete content). Review rows that name
  // specific sounds, like `Review sound/spellings: /m/, /t/, /d/, /a/`, ARE
  // valuable — they're real practice opportunities and worth indexing —
  // so we only drop reviews that don't reference any /sound/ notation.
  if (/^review/i.test(trimmed) && !/\/[a-zA-Z_]+\//.test(trimmed)) return false;
  if (/^student performance assessment$/i.test(trimmed)) return false;
  if (/^unit assessment/i.test(trimmed)) return false;
  return true;
}

function slugifyValue(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Canonical grade label used everywhere across the phonics-practice tree —
 * the hub, the curriculum scope-and-sequence, the per-objective hero,
 * grade filter chips, and lesson cards. Centralised so e.g. tags and
 * breadcrumbs on linked routes can't drift apart for the same objective.
 */
export function formatGradeLabel(grade: string): string {
  if (grade === 'K') return 'Kindergarten';
  if (grade === 'Pre-K') return 'Pre-K';
  if (grade === '1') return 'Grade 1';
  if (grade === '2') return 'Grade 2';
  if (grade === '3') return 'Grade 3';
  if (grade === '4') return 'Grade 4';
  if (grade === '5') return 'Grade 5';
  // Pass through curriculum-range markers like "K–5" or "Pre-K – 2" unchanged.
  if (/[––-]/.test(grade)) return grade;
  return `Grade ${grade}`;
}

/* ------------------------------------------------------------------ */
/*  Marketing copy shared by the hub page and the per-curriculum page */
/* ------------------------------------------------------------------ */

export type CurriculumMarketing = {
  /** Small uppercase tag above the curriculum title on hub + curriculum hero. */
  tag: string;
  /** One-line description used on the curriculum card on the hub. */
  description: string;
  /** Rounded marketing number shown big on the card. */
  lessonCount: string;
  /** Short label for the "Browse <X> →" link. */
  browseShortLabel: string;
  /** Range string used in the per-curriculum hero stat (e.g. "K-5"). */
  gradeRange: string;
  /** Standards alignment claim (e.g. "100% TEKS-aligned", "Science of reading"). */
  alignmentClaim: string;
  /** Three-word label for the alignment claim stat (e.g. "TEKS-aligned"). */
  alignmentStatLabel: string;
  /**
   * Curriculum-specific brand color used as the accent on the per-curriculum
   * page (headline second line, stat numbers, alignment check circles).
   * Falls back to Curipod orange for curricula without a known brand color.
   */
  brand: {
    /** Strong accent — used on headline text and check circles. */
    accent: string;
    /** Darker accent — used on eyebrows + numbers when contrast is needed. */
    accentDark: string;
  };
};

/** Default brand colors used for curricula without their own theme. */
const CURIPOD_BRAND = {
  accent: '#F99746', // theme.colors.orange
  accentDark: '#E58332', // theme.colors.orangeDark
};

export const CURRICULUM_MARKETING: Record<string, CurriculumMarketing> = {
  'Bluebonnet Learning': {
    tag: 'Texas HQIM · K-5',
    description:
      "Fills Bluebonnet's spiral grammar and encoding gaps with daily practice.",
    lessonCount: '301',
    browseShortLabel: 'Bluebonnet',
    gradeRange: 'K-5',
    alignmentClaim: '100%',
    alignmentStatLabel: 'TEKS-aligned',
    // Texas Bluebonnet — bright cornflower / TEA brand blue.
    brand: { accent: '#2D8FE0', accentDark: '#1D6FB8' },
  },
  'UFLI Foundations': {
    tag: 'K-1 Phonics',
    description:
      "Encoding practice aligned to UFLI's daily lesson sequence. Every sound, every lesson.",
    lessonCount: '148',
    browseShortLabel: 'UFLI',
    gradeRange: 'K-2',
    alignmentClaim: '100%',
    alignmentStatLabel: 'UFLI-aligned',
    // UF / UFLI — Florida Gators orange (close enough to Curipod orange).
    brand: { accent: '#FA4616', accentDark: '#C03612' },
  },
  'HMH Into Reading': {
    tag: 'K-5 ELA',
    description: 'Module-by-module phonics activities across the full K-5 scope.',
    lessonCount: '63',
    browseShortLabel: 'HMH',
    gradeRange: 'K-5',
    alignmentClaim: '100%',
    alignmentStatLabel: 'Module-mapped',
    // HMH — deep teal / navy from current Into Reading branding.
    brand: { accent: '#0C6F8C', accentDark: '#08526A' },
  },
  'Heggerty Phonemic Awareness': {
    tag: 'K-2 Phonemic Awareness',
    description:
      "Take Heggerty's oral phonemic awareness routines into written encoding practice.",
    lessonCount: '108',
    browseShortLabel: 'Heggerty',
    gradeRange: 'Pre-K – 2',
    alignmentClaim: '100%',
    alignmentStatLabel: 'Heggerty-aligned',
    // Heggerty — purple from their long-standing brand.
    brand: { accent: '#7B2FA6', accentDark: '#5E2380' },
  },
  'Amplify CKLA': {
    tag: 'K-5 Knowledge-Building',
    description: "Encoding practice aligned to CKLA's phonics scope and sequence.",
    lessonCount: '211',
    browseShortLabel: 'CKLA',
    gradeRange: 'K-2',
    alignmentClaim: '100%',
    alignmentStatLabel: 'CKLA-aligned',
    // Amplify — magenta / red-pink.
    brand: { accent: '#E5006D', accentDark: '#B30055' },
  },
  'Wilson Fundations': {
    tag: 'K-3 Structured Literacy',
    description:
      "Encoding practice that follows Fundations' scope and sequence, unit by unit.",
    lessonCount: '304',
    browseShortLabel: 'Fundations',
    gradeRange: 'K-3',
    alignmentClaim: '100%',
    alignmentStatLabel: 'Fundations-aligned',
    // Wilson Language — green from the Fundations brand.
    brand: { accent: '#3FA34D', accentDark: '#2E7E3A' },
  },
  Wonders: {
    tag: 'K-5 ELA',
    description:
      "Phonics encoding activities aligned to McGraw Hill Wonders' weekly skills.",
    lessonCount: '96',
    browseShortLabel: 'Wonders',
    gradeRange: 'K-2',
    alignmentClaim: '100%',
    alignmentStatLabel: 'Wonders-aligned',
    // McGraw Hill Wonders — corporate blue.
    brand: { accent: '#1F4E9C', accentDark: '#163A75' },
  },
  'Really Great Reading Phonics Boost': {
    tag: 'Tier 2/3 Intervention',
    description:
      "Encoding practice aligned to Phonics Boost's 78-lesson intervention sequence.",
    lessonCount: '78',
    browseShortLabel: 'RGR',
    gradeRange: 'Grades 1-5',
    alignmentClaim: '100%',
    alignmentStatLabel: 'Boost-aligned',
    // Really Great Reading — burgundy / red brand.
    brand: { accent: '#B32F36', accentDark: '#8C2329' },
  },
  'Savvas myView': {
    tag: 'K-5 ELA',
    description:
      "Encoding practice aligned to myView Literacy's unit-by-unit phonics sequence.",
    lessonCount: '179',
    browseShortLabel: 'Savvas',
    gradeRange: 'K-2',
    alignmentClaim: '100%',
    alignmentStatLabel: 'myView-aligned',
    // Savvas Learning — deep indigo / navy brand.
    brand: { accent: '#1A2E6E', accentDark: '#111F4D' },
  },
};

export { CURIPOD_BRAND };

/* ------------------------------------------------------------------ */
/*  Per-curriculum long-form copy for the detail page                 */
/* ------------------------------------------------------------------ */

export type CurriculumDetailCopy = {
  /** 3-5 short alignment chips shown under the hero ("Vowel teams", etc.). */
  alignmentChips: string[];
  /** Long-form "About the phonics scope" section. */
  scope: {
    title: string;
    paragraphs: string[];
    quote: string;
    meta: { key: string; value: string }[];
  };
  /** Two cards in the dark credibility band. */
  credibility: { title: string; body: string }[];
  /** FAQ items. */
  faq: { q: string; a: string }[];
  /** Slugs of 3 curricula to recommend at the bottom. */
  otherCurriculumSlugs: string[];
};

const BASE_OTHER_CURRICULA_BY_VALUE: Record<string, string[]> = {
  'Bluebonnet Learning': [
    'ufli-foundations',
    'hmh-into-reading',
    'heggerty-phonemic-awareness',
  ],
  'UFLI Foundations': [
    'bluebonnet-learning',
    'amplify-ckla',
    'heggerty-phonemic-awareness',
  ],
  'HMH Into Reading': ['bluebonnet-learning', 'amplify-ckla', 'wonders'],
  'Heggerty Phonemic Awareness': [
    'ufli-foundations',
    'bluebonnet-learning',
    'amplify-ckla',
  ],
  'Amplify CKLA': ['bluebonnet-learning', 'ufli-foundations', 'hmh-into-reading'],
  'Wilson Fundations': ['bluebonnet-learning', 'amplify-ckla', 'ufli-foundations'],
  Wonders: ['hmh-into-reading', 'bluebonnet-learning', 'ufli-foundations'],
  'Really Great Reading Phonics Boost': [
    'wilson-fundations',
    'ufli-foundations',
    'amplify-ckla',
  ],
  'Savvas myView': ['ufli-foundations', 'wonders', 'hmh-into-reading'],
};

/**
 * Detailed copy keyed by curriculum value. Bluebonnet is fully populated
 * from the approved design; the other curricula use templated defaults
 * generated from the curriculum name in `buildDefaultDetailCopy`.
 */
const CURRICULUM_DETAIL_OVERRIDES: Partial<Record<string, CurriculumDetailCopy>> = {
  'Bluebonnet Learning': {
    alignmentChips: [
      'Vowel teams & diphthongs',
      'Consonant digraphs & blends',
      'R-controlled vowels',
      'TEKS-aligned',
    ],
    scope: {
      title: "Bluebonnet's phonics scope, in brief.",
      paragraphs: [
        "Bluebonnet Learning is the Texas Education Agency's state-developed K-5 ELA curriculum — a High-Quality Instructional Material (HQIM) approved for use across Texas school districts. It's grounded in the science of reading, TEKS-aligned, and free for any Texas district to adopt.",
        "Bluebonnet's phonics scope is explicit and systematic. Kindergarten through Grade 2 covers letter-sound correspondences, blending and segmenting, digraphs, blends, vowel teams, diphthongs, and r-controlled vowels. Grades 3-5 transition to multisyllabic decoding and morphology — prefixes, suffixes, and root analysis.",
        'The phonics direct instruction is rigorous. But most teachers we talk to flag the same gap: not enough time for every student to actually practice the pattern after the lesson. Bluebonnet introduces the sound; the day runs short on application reps.',
      ],
      quote:
        "The Bluebonnet phonics lesson is great. I just can't get every kid enough turns to actually spell with the pattern.",
      meta: [
        { key: 'Publisher', value: 'Texas Education Agency' },
        { key: 'Phonics scope', value: 'K-2 foundational · 3-5 morphology' },
        { key: 'Approach', value: 'Science of reading, HQIM' },
        { key: 'Standards', value: 'TEKS-aligned' },
      ],
    },
    credibility: [
      {
        title: 'Aligned to TEKS and the science of reading',
        body:
          'Every Bluebonnet-aligned activity is tagged with the relevant TEKS code and built on the encoding research Bluebonnet was designed around.',
      },
      {
        title: "Fits right after Bluebonnet's direct instruction",
        body:
          "Drops in after the teacher introduces the new pattern. Functions as the productive application step Bluebonnet's pacing doesn't always allow for.",
      },
    ],
    faq: [
      {
        q: "Does Curipod replace Bluebonnet's phonics instruction?",
        a:
          "No. Curipod complements Bluebonnet's phonics block — it's the practice layer that drops in after the direct instruction. Teachers keep teaching the Bluebonnet phonics lesson exactly as written; Curipod gives every student a turn to apply what was just taught.",
      },
      {
        q: 'Are the activities TEKS-aligned?',
        a:
          'Yes. Every Bluebonnet-aligned activity is tagged with the relevant TEKS code, so you can map practice directly to the standards your district reports on.',
      },
      {
        q: 'How much time does it add to my lesson?',
        a:
          'Just a few minutes. Curipod is the productive application step that runs right after your direct instruction — timed activities keep the whole class moving together without adding prep.',
      },
      {
        q: 'Is Curipod aligned to the science of reading?',
        a:
          'Yes. Activities are built on the encoding research Bluebonnet was designed around: students segment, choose graphemes, write, and read — hitting every step the science says builds durable phonics knowledge.',
      },
      {
        q: 'Can I try Curipod with my class before my district adopts it?',
        a:
          "Absolutely. Teachers can start free and run a Bluebonnet-aligned activity with their class today. When you're ready, we'll help bring it to your campus or district.",
      },
    ],
    otherCurriculumSlugs: BASE_OTHER_CURRICULA_BY_VALUE['Bluebonnet Learning']!,
  },
};

export function getCurriculumDetailCopy(
  curriculum: CurriculumEntry,
): CurriculumDetailCopy {
  return (
    CURRICULUM_DETAIL_OVERRIDES[curriculum.value] ?? buildDefaultDetailCopy(curriculum)
  );
}

/**
 * Generic templated detail copy for curricula without a hand-written
 * override. Reads OK for SEO; editorial pass can replace any of these by
 * adding an entry to `CURRICULUM_DETAIL_OVERRIDES`.
 */
function buildDefaultDetailCopy(curriculum: CurriculumEntry): CurriculumDetailCopy {
  const name = curriculum.displayName;
  return {
    alignmentChips: [
      'Letter-sound correspondences',
      'Digraphs & blends',
      'Vowel teams',
      `${name} scope`,
    ],
    scope: {
      title: `${name}'s phonics scope, in brief.`,
      paragraphs: [
        `${name} is a widely adopted phonics scope and sequence used across K-5 classrooms. It moves students from letter-sound correspondences and CVC blending into digraphs, blends, vowel teams, and r-controlled vowels — building toward multisyllabic decoding.`,
        `${name}'s phonics direct instruction is rigorous. But most teachers flag the same gap: not enough time for every student to actually practice the pattern after the lesson. The new sound gets taught; the day runs short on application reps.`,
        'Curipod fills that gap — every student writes words with the target sound, gets instant feedback, and the teacher sees who got it before moving on.',
      ],
      quote: `The ${name} lesson is great. I just can't get every kid enough turns to actually spell with the pattern.`,
      meta: [
        { key: 'Approach', value: 'Science of reading' },
        { key: 'Grade band', value: 'K-5 phonics' },
        { key: 'Use case', value: 'Productive application' },
        { key: 'Alignment', value: `Mapped to ${name}` },
      ],
    },
    credibility: [
      {
        title: 'Aligned to the science of reading',
        body: `Every ${name}-mapped activity is built on the encoding research: students segment, choose graphemes, write, and read — hitting every step the science says builds durable phonics knowledge.`,
      },
      {
        title: `Fits right after ${name}'s direct instruction`,
        body: `Drops in after the teacher introduces the new pattern. Functions as the productive application step ${name}'s pacing doesn't always allow for.`,
      },
    ],
    faq: [
      {
        q: `Does Curipod replace ${name}'s phonics instruction?`,
        a: `No. Curipod complements ${name}'s phonics block — it's the practice layer that drops in after the direct instruction. Teachers keep teaching the ${name} lesson exactly as written; Curipod gives every student a turn to apply what was just taught.`,
      },
      {
        q: 'Is Curipod aligned to the science of reading?',
        a: `Yes. Activities are built on the encoding research ${name} was designed around: students segment, choose graphemes, write, and read — hitting every step the science says builds durable phonics knowledge.`,
      },
      {
        q: 'How much time does it add to my lesson?',
        a:
          'Just a few minutes. Curipod is the productive application step that runs right after your direct instruction — timed activities keep the whole class moving together without adding prep.',
      },
      {
        q: `Can I try Curipod with my ${name} class today?`,
        a: `Absolutely. Teachers can start free and run a ${name}-aligned activity with their class today. When you're ready, we'll help bring it to your campus or district.`,
      },
    ],
    otherCurriculumSlugs:
      BASE_OTHER_CURRICULA_BY_VALUE[curriculum.value] ??
      CURRICULA.filter(c => c.value !== curriculum.value)
        .slice(0, 3)
        .map(c => c.slug),
  };
}
